<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useDoubleTap, useDraggableZoom, usePinch } from '@internal/composables'
import type { MediaItem } from '@storefront-satch/components/molecules/gallery/types'

const props = defineProps<{
  item: MediaItem
  zoomActiveInherited: boolean
}>()

const emit = defineEmits<{
  zoomDisabled: [void]
  zoomEnabled: [void]
  pinchActive: [boolean]
}>()

const zoomContainer = ref()
const dragContainer = ref()
const wrapper = ref()
const zoomActive = ref<boolean>(false)
const pinchActive = ref<boolean>(false)

const toggleZoom = () => {
  zoomActive.value = !zoomActive.value
}

useDoubleTap(wrapper, toggleZoom)
usePinch(wrapper, {
  callback: () => {
    zoomActive.value = true
  },
  unpinchCallback: () => {
    zoomActive.value = false
  },
  onPinchStart: () => {
    emit('pinchActive', true)
    pinchActive.value = true
  },
  onPinchEnd: () => {
    emit('pinchActive', false)
    pinchActive.value = false
  },
})

const isDragEnabled = computed(() => zoomActive.value && !pinchActive.value)

const { xRef, yRef, isDragging } = useDraggableZoom(
  zoomContainer,
  wrapper,
  dragContainer,
  isDragEnabled
)

watch(
  () => props.zoomActiveInherited,
  (value) => {
    if (!value) {
      zoomActive.value = false
    }
  }
)

watch(zoomActive, (value) => {
  if (!value) {
    emit('zoomDisabled')
  } else {
    emit('zoomEnabled')
  }
})
</script>

<template>
  <div
    ref="wrapper"
    class="flex h-full"
  >
    <div
      ref="zoomContainer"
      :style="zoomActive && `transform: translate(${xRef}px, ${yRef}px);`"
      class="relative flex w-full touch-none justify-center"
      :class="{ 'translate-x-0 translate-y-0': !zoomActive }"
    >
      <img
        ref="dragContainer"
        class="relative max-h-full origin-center self-start object-cover transition duration-500 ease-out"
        :class="[
          !isDragging && 'transition',
          {
            'scale-[2.5]': zoomActive && props.item.isScalable,
            'scale-[2]': zoomActive && !props.item.isScalable,
            'scale-125': !zoomActive && props.item.isScalable,
            'w-full': !props.item.isScalable,
          },
        ]"
        :srcset="`${props.item.url}?io=transform:extend,width:1000,height:1000 1x, ${props.item.url}?io=transform:extend,width:1600,height:1600 2x`"
        loading="lazy"
      />
    </div>
  </div>
</template>
